import React from 'react';

import classNames from 'classnames';

import { serializerFlexAlignment } from '../../utils/serializerFlexAlignment';

import type { Testimonial, TestimonialCardSchema, TTestimonialAttributes } from '../Testimonials.types';
import Description from './Description';
import Image from './Image';
import Message from './Message';
import Name from './Name';

const cardSchema: TestimonialCardSchema = {
  node: 'div',
  className: 'flex flex-col gap-2',
  children: [
    {
      node: 'image',
      className: '',
      children: [],
    },
    {
      node: 'name',
      className: 'flex flex-col',
      children: [],
    },
    {
      node: 'description',
      className: 'flex flex-col',
      children: [],
    },
    {
      node: 'message',
      className: 'flex flex-col',
      children: [],
    },
  ],
};

interface SerializerProps {
  testimonial: Testimonial;
  attributes: TTestimonialAttributes;
}

const Serializer: React.FC<SerializerProps> = ({ testimonial, attributes }) => {
  const renderNode = (node: TestimonialCardSchema): React.ReactNode => {
    switch (node.node) {
      case 'div': {
        const flexAlignmentClass = serializerFlexAlignment({
          alignment: attributes?.alignment || 'left',
          includesFlexRow: node.className.includes('flex-row'),
          includesFlexColumn: node.className.includes('flex-col'),
        });

        return (
          <div className={classNames(node.className, flexAlignmentClass)}>
            {node.children.map((child, index) => (
              <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
            ))}
          </div>
        );
      }
      case 'image':
        return (
          <Image
            className={classNames(node.className)}
            url={testimonial?.picture_url}
            alt={testimonial?.name}
            attributes={attributes}
          />
        );
      case 'name':
        return <Name className={classNames(node.className)} name={testimonial?.name} attributes={attributes} />;
      case 'description':
        return (
          <Description
            className={classNames(node.className)}
            description={testimonial?.description}
            attributes={attributes}
          />
        );
      case 'message':
        return (
          <Message className={classNames(node.className)} message={testimonial?.message} attributes={attributes} />
        );
      default:
        return null;
    }
  };

  return <>{renderNode(attributes?.cardStructure || cardSchema)}</>;
};

export default Serializer;
